import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { LogOut, Plus } from 'lucide-react'

import Input from '../components/Input'
import Button from '../components/Button'
import Loading from '../components/Loading'
import DataTable from '../components/DataTable'
import Select from '../components/Select'
import { addUser, getUsers, forceLogoutUser, forceLogoutMultipleUsers } from '../actions/userActions'
import { getRoles } from '../actions/roleActions'
import {
  ADD_USER_RESET,
  GET_USERS_RESET,
  FORCE_LOGOUT_USER_RESET,
  FORCE_LOGOUT_MULTIPLE_RESET,
} from '../constants/userConstants'

const UserList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([])
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])

  // Add a User
  const [openModal, setOpenModal] = useState(false)
  const [email, setEmail] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [passwordModal, setPasswordModal] = useState(false)

  // Force Logout States
  const [forceLogoutModal, setForceLogoutModal] = useState(false)
  const [selectedUserForLogout, setSelectedUserForLogout] = useState(null)
  const [selectedUsersForBulkLogout, setSelectedUsersForBulkLogout] = useState([])
  const [bulkForceLogoutModal, setBulkForceLogoutModal] = useState(false)
  const [rowSelection, setRowSelection] = useState({})

  // Roles
  const [addUserOption, setAddUserOption] = useState(true)
  const [forceLogoutOption, setForceLogoutOption] = useState(true)
  const [loading, setLoading] = useState(true)

  const dispatch = useDispatch()

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  useEffect(() => {
    if (!userInfo) {
      navigate('/')
    }
  }, [userInfo, navigate])

  // Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  useEffect(() => {
    if (getRoleData) {
      setAddUserOption(getRoleData.usersAdd)
      setForceLogoutOption(getRoleData.usersUpdate)
      if (!getRoleData.usersView) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/')
      }
    }
  }, [getRoleData, navigate])

  useEffect(() => {
    dispatch(getUsers())
    dispatch(getRoles())
  }, [dispatch])

  // Get Users
  const getUsersInfo = useSelector((state) => state.getUsersInfo)
  const { errorGetUsersInfo, getUsersData } = getUsersInfo

  useEffect(() => {
    dispatch({ type: GET_USERS_RESET })
    if (getUsersData) {
      setLoading(false)
      let usersData = getUsersData.users || getUsersData
      if (Array.isArray(usersData)) {
        setData(usersData)
      } else if (usersData && typeof usersData === 'object') {
        const possibleUserArrays = Object.values(usersData).filter(item => Array.isArray(item))
        if (possibleUserArrays.length > 0) {
          setData(possibleUserArrays[0])
        } else {
          setData([])
        }
      } else {
        setData([])
      }
    } else if (errorGetUsersInfo) {
      setLoading(false)
      toast(errorGetUsersInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getUsersData, errorGetUsersInfo, dispatch])

  // Get roles data
  const getRolesInfo = useSelector((state) => state.getRolesInfo)
  const { getRolesData } = getRolesInfo

  useEffect(() => {
    if (getRolesData && Array.isArray(getRolesData)) {
      setRolesFromServer(getRolesData)
    }
  }, [getRolesData])

  useEffect(() => {
    if (Array.isArray(rolesFromServer) && rolesFromServer.length > 0) {
      const data = rolesFromServer.map((item) => ({
        id: item._id,
        title: item.name,
      }))
      setRoleOptions(data)
    }
  }, [rolesFromServer])

  // Force Logout Single User
  const openForceLogoutModal = (user) => {
    setSelectedUserForLogout(user)
    setForceLogoutModal(true)
  }

  const closeForceLogoutModal = () => {
    setSelectedUserForLogout(null)
    setForceLogoutModal(false)
  }

  const handleForceLogout = () => {
    if (selectedUserForLogout) {
      dispatch(forceLogoutUser(selectedUserForLogout._id))
    }
  }

  // Force Logout Multiple Users
  const openBulkForceLogoutModal = () => {
    if (selectedUsersForBulkLogout.length === 0) {
      toast('Please select users to force logout', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setBulkForceLogoutModal(true)
  }

  const closeBulkForceLogoutModal = () => {
    setBulkForceLogoutModal(false)
  }

  const handleBulkForceLogout = () => {
    const userIds = selectedUsersForBulkLogout.map(user => user._id)
    dispatch(forceLogoutMultipleUsers(userIds))
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'select',
        header: ({ table }) => (
          forceLogoutOption && (
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              className="w-4 h-4"
            />
          )
        ),
        cell: ({ row }) => (
          forceLogoutOption && userInfo?.id !== row.original._id && (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              className="w-4 h-4"
            />
          )
        ),
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            className='font-bold text-md cursor-pointer text-green-800 bg-green-100 px-3 py-1 rounded inline-block hover:bg-green-200 transition-colors'
            to={`/users/${row.original._id}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'identifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'role.name',
        header: 'Role',
        cell: ({ row }) => row.original.role?.name || 'N/A',
      },
      {
        accessorKey: 'joinedOn',
        header: 'Joined On',
        cell: ({ row }) => {
          if (row.original.joinedOn) {
            try {
              return new Date(row.original.joinedOn).toLocaleDateString()
            } catch (error) {
              return row.original.joinedOn
            }
          }
          return 'N/A'
        },
      },
      {
        accessorKey: 'calling',
        header: 'Status',
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded text-xs font-medium ${row.original.calling
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
            }`}>
            {row.original.calling ? 'Online' : 'Offline'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex gap-2'>
            {forceLogoutOption && userInfo?.id !== row.original._id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openForceLogoutModal(row.original);
                }}
                className='bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1'
                title='Force Logout'
              >
                <LogOut size={14} />
                Logout
              </button>
            )}
          </div>
        ),
        enableSorting: false,
      },
    ],
    [forceLogoutOption, userInfo]
  )

  // Open Add User Modal
  const openAddUserModal = () => {
    setOpenModal(true)
  }

  // Close Add User Modal
  const closeAddUserModal = () => {
    setOpenModal(false)
    setEmail('')
    setIdentifier('')
    setName('')
    setRole('')
    setIpAddress('')
  }

  // Add User
  const addUserHandler = () => {
    if (!name || !identifier || !email || !role || !ipAddress) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    dispatch(addUser(name, identifier, email, role, ipAddress))
  }

  const addUserInfo = useSelector((state) => state.addUserInfo)
  const { loadingAddUserInfo, errorAddUserInfo, addUserData } = addUserInfo

  useEffect(() => {
    dispatch({ type: ADD_USER_RESET })
    if (addUserData) {
      toast('User added successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setNewPassword(addUserData)
      setPasswordModal(true)
      closeAddUserModal()
    } else if (errorAddUserInfo) {
      toast(errorAddUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [addUserData, errorAddUserInfo, dispatch])

  // Force Logout Handlers
  const forceLogoutUserInfo = useSelector((state) => state.forceLogoutUserInfo)
  const { loadingForceLogoutUser, errorForceLogoutUser, forceLogoutUserData } = forceLogoutUserInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_USER_RESET })
    if (forceLogoutUserData) {
      toast(forceLogoutUserData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      closeForceLogoutModal()
      setTimeout(() => {
        dispatch(getUsers())
      }, 1000)
    } else if (errorForceLogoutUser) {
      toast(errorForceLogoutUser, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutUserData, errorForceLogoutUser, dispatch])

  const forceLogoutMultipleInfo = useSelector((state) => state.forceLogoutMultipleInfo)
  const { loadingForceLogoutMultiple, errorForceLogoutMultiple, forceLogoutMultipleData } = forceLogoutMultipleInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_MULTIPLE_RESET })
    if (forceLogoutMultipleData) {
      toast(forceLogoutMultipleData.message || 'Users logged out successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      setSelectedUsersForBulkLogout([])
      setRowSelection({})
      closeBulkForceLogoutModal()
      setTimeout(() => {
        dispatch(getUsers())
      }, 1000)
    } else if (errorForceLogoutMultiple) {
      toast(errorForceLogoutMultiple, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutMultipleData, errorForceLogoutMultiple, dispatch])

  // Update selected users when row selection changes
  useEffect(() => {
    const selectedIndexes = Object.keys(rowSelection).filter(key => rowSelection[key])
    const selectedUsers = selectedIndexes.map(index => data[parseInt(index)])
    setSelectedUsersForBulkLogout(selectedUsers)
  }, [rowSelection, data])

  const closePasswordModal = () => {
    setNewPassword('')
    setPasswordModal(false)
    setTimeout(() => {
      dispatch(getUsers())
    }, 1000)
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className='w-full h-full'>
      <h1 className='text-2xl font-semibold mb-4'>Users</h1>

      <DataTable
        data={data}
        columns={columns}
        title="User Management"
        isLoading={loading}
        toolbar={
          <>
            {forceLogoutOption && (
              <Button
                type='button'
                onClick={openBulkForceLogoutModal}
                text='Force Logout Selected'
                className='bg-orange-500 hover:bg-orange-700'
              />
            )}
            {addUserOption && (
              <Button
                type='button'
                onClick={openAddUserModal}
                text={<span className="flex items-center gap-2"><Plus size={16} />Add User</span>}
                className='bg-emerald-500 hover:bg-emerald-700'
              />
            )}
          </>
        }
      />

      {/* Add User Modal */}
      {openModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-xl mx-auto bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Add User</h3>
              </div>
              <div className="px-4 py-4">
                <div className="flex gap-4 mb-4">
                  <Input
                    width="flex-1"
                    name="Name *"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Input
                    width="flex-1"
                    name="Identifier *"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>
                <Input
                  width="w-full mb-4"
                  name="Email *"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Select
                  width="w-full mb-4"
                  name="Role *"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  options={roleOptions}
                />
                <Input
                  width="w-full mb-4"
                  name="IP Address *"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-2">
                  All the fields with * are mandatory
                </p>
              </div>
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeAddUserModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={addUserHandler}
                  disabled={loadingAddUserInfo}
                  className={`${loadingAddUserInfo ? 'bg-gray-300' : 'bg-emerald-500 hover:bg-emerald-600'
                    } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Password Modal */}
      {passwordModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-3xl'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>New Password</h3>
                </div>
                <div className='relative p-6 pb-2 flex-auto'>
                  <Input
                    width='w-full mb-4'
                    name='New Password *'
                    value={newPassword}
                    disabled
                  />
                </div>
                <div className='flex items-center justify-end py-2 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closePasswordModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Force Logout Single User Modal */}
      {forceLogoutModal && selectedUserForLogout && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout User</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to force logout <strong>{selectedUserForLogout.name}</strong>?
                  </p>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleForceLogout}
                    disabled={loadingForceLogoutUser}
                  >
                    Force Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Bulk Force Logout Modal */}
      {bulkForceLogoutModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout Multiple Users</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed mb-3'>
                    Are you sure you want to force logout <strong>{selectedUsersForBulkLogout.length}</strong> selected users?
                  </p>
                  <div className='max-h-32 overflow-y-auto'>
                    {selectedUsersForBulkLogout.map((user, index) => (
                      <p key={index} className='text-xs text-gray-600'>• {user.name} ({user.email})</p>
                    ))}
                  </div>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeBulkForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleBulkForceLogout}
                    disabled={loadingForceLogoutMultiple}
                  >
                    Force Logout All
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}
    </div>
  )
}

export default UserList
